import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import * as printJS from 'print-js';
import { ApiService } from './api.service';
import { SuccessDialogComponent } from '../shared/components/success-dialog/success-dialog.component';
import { b64toBlob } from '../utils/file-utils';

export type OriginalDocumentPdfOptions = {
  setLoading?: (loading: boolean) => void;
  onError?: (message: string) => void;
  fileName?: string;
  labels?: Record<string, string> | null;
  /** Sign-link (no portal session): full page URL for download auth. */
  url_list?: string;
};

export type IosPdfSaveOutcome = 'shared' | 'failed' | 'cancelled';

/**
 * Fetch original (unmasked) PDF via docmgmt/downloadDocument, then print or download.
 * Shared so parents stay thin (document-management now; reusable later).
 */
@Injectable({ providedIn: 'root' })
export class OriginalDocumentPdfService {
  static readonly IOS_SAVE_TIP =
    'On iPhone, use Share and choose Save to Files, or open the link in Safari for direct download.';

  constructor(
    private readonly apiService: ApiService,
    private readonly dialog: MatDialog
  ) {}

  isIOSDevice(): boolean {
    const ua = window.navigator.userAgent || '';
    const iOSUA = /iPad|iPhone|iPod/.test(ua);
    const iPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    return iOSUA || iPadOS;
  }

  async fetchOriginal(
    documentReference: string,
    options: OriginalDocumentPdfOptions = {}
  ): Promise<{ base64: string; fileName: string } | null> {
    const ref = String(documentReference || '').trim();
    if (!ref) {
      options.onError?.(options.labels?.['something_went_wrong'] ?? 'Document reference is missing.');
      return null;
    }

    options.setLoading?.(true);
    try {
      const req: { document_reference: string; url_list?: string } = { document_reference: ref };
      const urlList = String(options.url_list || '').trim();
      if (urlList) {
        req.url_list = urlList;
      }
      const res = await firstValueFrom(
        this.apiService.downloadDocument(req)
      );
      const raw = String(res?.document_content ?? '').trim();
      if (!raw) {
        options.onError?.(
          options.labels?.['something_went_wrong'] ?? 'The download response did not include a document.'
        );
        return null;
      }
      return {
        base64: raw.replace(/^data:[^;]+;base64,/, ''),
        fileName: this.resolveFileName(res?.document_file_name, options.fileName),
      };
    } catch {
      options.onError?.(options.labels?.['something_went_wrong'] ?? 'Unable to download the document.');
      return null;
    } finally {
      options.setLoading?.(false);
    }
  }

  async print(documentReference: string, options: OriginalDocumentPdfOptions = {}): Promise<void> {
    const payload = await this.fetchOriginal(documentReference, options);
    if (!payload) return;
    printJS({ printable: payload.base64, type: 'pdf', base64: true });
  }

  async download(documentReference: string, options: OriginalDocumentPdfOptions = {}): Promise<void> {
    const payload = await this.fetchOriginal(documentReference, options);
    if (!payload) return;

    if (this.isIOSDevice()) {
      const outcome = await this.promptIosPdfSave(payload.base64, payload.fileName, options.labels);
      if (outcome === 'failed') {
        // options.onError?.(options.labels?.['ios_save_share_tip'] || OriginalDocumentPdfService.IOS_SAVE_TIP);
      }
      return;
    }

    const ok = await this.downloadPdfFromBase64(payload.base64, payload.fileName);
    if (!ok) {
      options.onError?.(options.labels?.['something_went_wrong'] ?? 'Unable to download the document.');
    }
  }

  /**
   * Save/share PDF from base64. On iOS uses navigator.share under the caller’s user gesture.
   * Public so SuccessDialog can run share on the Save tap (web-form QDA-7342 pattern).
   */
  async downloadPdfFromBase64(base64: string, fileName: string): Promise<boolean> {
    let blob: Blob | null = null;
    try {
      blob = b64toBlob(base64, 'application/pdf');
    } catch {
      return false;
    }
    if (!blob) return false;

    if (this.isIOSDevice()) {
      return this.tryShareFile(blob, fileName);
    }

    const url = URL.createObjectURL(blob) + '#toolbar=0&navpanes=0&scrollbar=0&view=FitH';
    const a = document.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute('target', '_blank');
    a.setAttribute('download', fileName || 'document.pdf');
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
    return true;
  }

  private async promptIosPdfSave(
    base64: string,
    fileName: string,
    labels?: Record<string, string> | null
  ): Promise<IosPdfSaveOutcome> {
    const result = await firstValueFrom(
      this.dialog
        .open(SuccessDialogComponent, {
          width: '85vw',
          maxWidth: '450px',
          panelClass: 'qwd-info-dialog',
          data: {
            message:
              labels?.['ios_save_ready_message'] ||
              'Save this document to your device',
            isSuccess: false,
            isSavePrompt: true,
            actions: {
              confirmLabel: labels?.['save'] || 'Save',
              cancelLabel: labels?.['cancel'] || 'Cancel',
            },
            savePayload: { base64, fileName },
          },
        })
        .afterClosed()
    );
    if (result === 'shared') return 'shared';
    if (result === 'failed') return 'failed';
    return 'cancelled';
  }

  private async tryShareFile(blob: Blob, fileName: string): Promise<boolean> {
    try {
      const nav: any = navigator;
      if (!nav?.share || typeof File === 'undefined') return false;
      const file = new File([blob], fileName, { type: blob.type || 'application/pdf' });
      if (nav.canShare && !nav.canShare({ files: [file] })) return false;
      // files-only: iOS treats `title` as a second text item and saves an extra .txt
      await nav.share({ files: [file] });
      return true;
    } catch {
      return false;
    }
  }

  private resolveFileName(apiName?: string, fallback?: string): string {
    const fromApi = String(apiName || '').trim();
    if (fromApi) {
      return fromApi.toLowerCase().endsWith('.pdf') ? fromApi : `${fromApi}.pdf`;
    }
    const base = String(fallback || 'document').replace(/\.pdf$/i, '').trim() || 'document';
    return `${base}.pdf`;
  }
}
